import { BrandWordmark } from "./BrandWordmark";
import { OptimizedImage } from "@/components/ui/optimized-image";
import { useTranslation } from "react-i18next";
import { Link } from "wouter";
import { useAuth } from "@/hooks/use-auth";

export function AppFooter() {
  const { t } = useTranslation();
  // Signed-in users go straight to the feature; logged-out visitors are sent
  // to sign-up (the footer also renders on the public landing page).
  const { user } = useAuth();
  const productHref = (path: string) => (user ? path : "/sign-up");
  return (
    <footer aria-label="Site footer" className="bg-muted/30 border-t" data-testid="section-footer">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="mb-8">
          <div className="mb-8">
            <div className="flex items-center gap-2.5 mb-4">
              <OptimizedImage
                src="/troopn-logo.png"
                alt="TroopN"
                width={36}
                height={36}
                className="w-9 h-9 rounded-full"
                data-testid="img-footer-logo"
              />
              <div className="flex flex-col leading-tight">
                <BrandWordmark />
                <div className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-brand-navy" />
                  <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-accent-app" />
                  <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-brand-coral" />
                </div>
              </div>
            </div>
            <p className="font-serif italic text-base text-brand-coral mb-2">
              {t('footer.brandLine')}
            </p>
            <p className="text-sm text-muted-foreground max-w-xs">
              {t('footer.tagline')}
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8">
            <div>
              <h4 className="font-semibold mb-3 text-sm">{t('footer.product')}</h4>
              <ul className="space-y-2 text-sm">
                <li><Link href={productHref("/discover")} className="text-muted-foreground hover:text-foreground" data-testid="link-footer-discover-trips">{t('footer.discoverTrips')}</Link></li>
                <li><Link href={productHref("/create-trip")} className="text-muted-foreground hover:text-foreground" data-testid="link-footer-ai-planner">{t('footer.aiPlanner')}</Link></li>
                <li><Link href={productHref("/travel-calendar")} className="text-muted-foreground hover:text-foreground" data-testid="link-footer-travel-calendar">{t('footer.travelCalendar')}</Link></li>
                <li>{user
                  ? <Link href="/premium" className="text-muted-foreground hover:text-foreground" data-testid="link-footer-premium">{t('footer.premiumPlans')}</Link>
                  : <a href="#features" className="text-muted-foreground hover:text-foreground" data-testid="link-footer-premium">{t('footer.premiumPlans')}</a>}</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-3 text-sm">{t('footer.company')}</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/about" className="text-muted-foreground hover:text-foreground" data-testid="link-footer-about">{t('footer.aboutUs')}</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-3 text-sm">{t('footer.support')}</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/help-center" className="text-muted-foreground hover:text-foreground" data-testid="link-footer-help-center">{t('footer.helpCenter')}</a></li>
                <li><a href="/contact" className="text-muted-foreground hover:text-foreground" data-testid="link-footer-contact">{t('footer.contactUs')}</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-3 text-sm">{t('footer.legal')}</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/terms-of-service" className="text-muted-foreground hover:text-foreground" data-testid="link-footer-terms">{t('footer.terms')}</a></li>
                <li><a href="/privacy-policy" className="text-muted-foreground hover:text-foreground" data-testid="link-footer-privacy">{t('footer.privacy')}</a></li>
                <li><a href="/messaging-terms" className="text-muted-foreground hover:text-foreground" data-testid="link-footer-messaging-terms">{t('footer.messagingTerms')}</a></li>
                <li><a href="/cookie-policy" className="text-muted-foreground hover:text-foreground" data-testid="link-footer-cookies">{t('footer.cookies')}</a></li>
                <li><a href="/do-not-sell" className="text-muted-foreground hover:text-foreground" data-testid="link-footer-do-not-sell">{t('footer.doNotSell')}</a></li>
                <li><a href="/ai-disclosure" className="text-muted-foreground hover:text-foreground" data-testid="link-footer-ai-disclosure">{t('footer.aiDisclosure')}</a></li>
                <li><a href="/accessibility" className="text-muted-foreground hover:text-foreground" data-testid="link-footer-accessibility">{t('footer.accessibility')}</a></li>
              </ul>
            </div>
          </div>
        </div>
        
        <div className="border-t pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-sm text-muted-foreground">
            {t('footer.copyright')}
          </p>
          <p className="text-xs text-muted-foreground">
            Troopn Travel Co. is a brand of Troopn LLC, a Florida limited liability company.
          </p>
        </div>
      </div>
    </footer>
  );
}
